import React, {useState} from 'react';
import {
    ActionButton,
    BrowseLabel,
    DialogEditActionsWrapper,
    DialogWrapper,
    Label,
    TextWrapper
} from './UploadFileSelectionDialog.style';
import {DragAndDrop} from '../../DragAndDrop/DragAndDrop';
import {t} from 'i18next';
import {Accordion} from '../../Accordion/Accordion';
import {
    getFontSize,
    isValidText,
    pathToResource,
    SPECIAL_CHARACTERS,
    SPECIAL_END_CHARACTERS,
    validateContainsSpecialCharacter,
    validateEndWithSpecialCharacter
} from '../../../utils';
import {Valid} from '../../validation/Valid';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {UploadSelectionFileDialogProps} from '../StorageObjectDialogProps';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {getApplicableTemplateMetadataPropertySettingsMap, isVisibleComponent} from '../../../utils/TemplateUtils';
import {ObjectType} from '../../../models/ObjectType';
import {STORAGE_OBJECT_PATH} from '../../../init/storage_object_constants';

/**
 * @name UploadFileSelectionDialog
 * @description
 * The dialog to select a new file to upload
 */
export const UploadFileSelectionDialog: React.VFC<UploadSelectionFileDialogProps> = (props) => {
    const [templatePropertySettings] = useState<Map<string, TemplatePropertySettings>>(getApplicableTemplateMetadataPropertySettingsMap(ObjectType.FILE));
    const [file, setFile] = useState<File | null>(null);
    const handleFile = (file: File | null) => {
        setFile(file);
    };

    let validatedNameInputSpecialCharacter = (!file) ? true : ((!validateContainsSpecialCharacter(file.name)) && (!validateEndWithSpecialCharacter(file.name)));
    let validatedNameInputEndSpecialCharacter = (!file) ? true : (!validateEndWithSpecialCharacter(file.name));
    let validationNameInputMessage = (!validatedNameInputEndSpecialCharacter) ? t('ERROR_FILE_NAME_SPECIAL_END_CHARACTER_CONSTRAINT', {param1: SPECIAL_END_CHARACTERS}) : t('ERROR_FILE_NAME_SPECIAL_CHARACTER_CONSTRAINT', {param1: SPECIAL_CHARACTERS});

    const validateNameInput = (): boolean => {
        return (file && isValidText(file.name) && (file.name.trim().length > 0) && validatedNameInputSpecialCharacter && validatedNameInputEndSpecialCharacter) as boolean;
    };

    let validatedFileAndNameInput = file && validateNameInput();

    const handleUpload = () => {
        if (file && validatedFileAndNameInput) {
            props.onContinue(file);
        }
    };

    let selectFileId = generateIdentifier('select-file');
    let fontSize = getFontSize([
        t('UPLOAD_FILE_SELECTION_DIALOG_UPLOAD_LABEL'),
        t('UPLOAD_FILE_SELECTION_DIALOG_BROWSE_FILES_LABEL')
    ], 10);

    return (
        <DialogWrapper id={generateIdentifier()}
                       key={'key'}
                       modal
                       open
                       title={t('UPLOAD_FILE_SELECTION_DIALOG_TITLE')}
                       onClose={props.onClose}>
            <TextWrapper id={generateIdentifier()}>
                <p id={generateIdentifier()}>{t('UPLOAD_FILE_SELECTION_DIALOG_MOVING_FILES_WARNING')}</p>
                {isVisibleComponent(STORAGE_OBJECT_PATH, templatePropertySettings) &&
                    <p id={generateIdentifier()}>
                        {t('UPLOAD_FILE_SELECTION_DIALOG_FILE_PATH_INFO_TEXT', {path: decodeURI(pathToResource(props.breadcrumbs))})}
                    </p>
                }
            </TextWrapper>
            <Accordion accordionHeadline={t('UPLOAD_FILE_SELECTION_DIALOG_ACCORDION_TITLE_CHOOSE_FILE')}
                       startOpen>
                <Valid isValid={validatedNameInputSpecialCharacter && validatedNameInputEndSpecialCharacter}
                       message={validationNameInputMessage}>
                    <TextWrapper id={generateIdentifier()}>
                        {t('UPLOAD_FILE_SELECTION_DIALOG_DROP_OR_BROWSE_INFO_TEXT')}
                    </TextWrapper>
                    <DragAndDrop browseFile={file}
                                 acceptImagesOnly={false}
                                 onChange={handleFile}/>
                </Valid>
            </Accordion>
            <DialogEditActionsWrapper id={generateIdentifier()} className='m-dialog__actions'>
                <input id={selectFileId}
                       type='file'
                       style={{display: 'none'}}
                       onChange={(e) => setFile(e.target.files ? e.target.files[0] : null)}/>
                <Label id={generateIdentifier()} htmlFor={selectFileId}>
                    <BrowseLabel id={generateIdentifier()}
                                 className='a-button a-button--secondary'
                                 fontSize={fontSize}>
                        <i id={generateIdentifier()} className='a-icon a-button__icon boschicon-bosch-ic-folder-add'/>
                        <div id={generateIdentifier()}
                             className='a-button__label'>{t('UPLOAD_FILE_SELECTION_DIALOG_BROWSE_FILES_LABEL')}</div>
                    </BrowseLabel>
                </Label>
                <div id={generateIdentifier()}/>
                <ActionButton id={generateIdentifier()}
                              key='upload'
                              icon='upload'
                              mode='primary'
                              type='button'
                              label={t('UPLOAD_FILE_SELECTION_DIALOG_UPLOAD_LABEL')}
                              title={t('UPLOAD_FILE_SELECTION_DIALOG_UPLOAD_LABEL')}
                              fontSize={fontSize}
                              disabled={!validatedFileAndNameInput}
                              onClick={handleUpload}/>
            </DialogEditActionsWrapper>
        </DialogWrapper>
    );
};
